
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Mail, Lock, User, Phone, MapPin, Building, Flag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/components/LanguageContext";
import { Separator } from "@/components/ui/separator";

const trainerSignupSchema = z.object({
  firstName: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  lastName: z.string().min(2, "Sobrenome deve ter pelo menos 2 caracteres"),
  email: z.string().email("Formato de email inválido"),
  password: z.string().min(6, "A senha deve ter pelo menos 6 caracteres"),
  country: z.string().min(1, "País é obrigatório"),
  city: z.string().min(1, "Cidade é obrigatória"),
  phone: z.string().min(1, "Contacto telefónico é obrigatório"),
  termsAccepted: z.boolean().refine(val => val === true, {
    message: "Você deve concordar com os termos"
  })
});

const studentSignupSchema = z.object({
  firstName: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  lastName: z.string().min(2, "Sobrenome deve ter pelo menos 2 caracteres"),
  email: z.string().email("Formato de email inválido"),
  password: z.string().min(6, "A senha deve ter pelo menos 6 caracteres"),
  country: z.string().min(1, "País é obrigatório"),
  city: z.string().min(1, "Cidade é obrigatória"),
  termsAccepted: z.boolean().refine(val => val === true, {
    message: "Você deve concordar com os termos"
  })
});

type SignupFormProps = {
  onToggleForm: () => void;
};

export const SignupForm = ({
  onToggleForm
}: SignupFormProps) => {
  const [accountType, setAccountType] = useState<"trainer" | "student">("trainer");
  const isMobile = useIsMobile();
  const { t, language } = useLanguage();
  
  const trainerForm = useForm<z.infer<typeof trainerSignupSchema>>({
    resolver: zodResolver(trainerSignupSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      country: "",
      city: "",
      phone: "",
      termsAccepted: false
    }
  });
  
  const studentForm = useForm<z.infer<typeof studentSignupSchema>>({
    resolver: zodResolver(studentSignupSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      country: "",
      city: "",
      termsAccepted: false
    }
  });
  
  const onSubmitTrainer = async (values: z.infer<typeof trainerSignupSchema>) => {
    console.log("Tentando criar conta de treinador com:", values);
    try {
      const {
        data,
        error
      } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            first_name: values.firstName,
            last_name: values.lastName,
            is_trainer: true,
            is_student: false,
            phone: values.phone,
            country: values.country,
            city: values.city
          }
        }
      });
      if (error) {
        console.error("Erro no cadastro:", error);
        if (error.message.includes("already registered")) {
          toast.error(t('auth.errors.emailRegistered'));
        } else {
          toast.error(t('auth.errors.generalError'));
        }
        return;
      }
      if (!data.user) {
        throw new Error("Erro ao criar usuário");
      }
      console.log("Cadastro bem sucedido:", data);
      toast.success(t('auth.success.accountCreated'));
      onToggleForm();
    } catch (error: any) {
      console.error("Erro inesperado no cadastro:", error);
      toast.error(t('auth.errors.unexpectedError'));
    }
  };
  
  const onSubmitStudent = async (values: z.infer<typeof studentSignupSchema>) => {
    console.log("Tentando criar conta de aluno com:", values);
    try {
      const {
        data,
        error
      } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            first_name: values.firstName,
            last_name: values.lastName,
            is_trainer: false,
            is_student: true,
            country: values.country,
            city: values.city
          }
        }
      });
      if (error) {
        console.error("Erro no cadastro:", error);
        if (error.message.includes("already registered")) {
          toast.error(t('auth.errors.emailRegistered'));
        } else {
          toast.error(t('auth.errors.generalError'));
        }
        return;
      }
      if (!data.user) {
        throw new Error("Erro ao criar usuário");
      }
      console.log("Cadastro bem sucedido:", data);
      toast.success(t('auth.success.accountCreated'));
      onToggleForm();
    } catch (error: any) {
      console.error("Erro inesperado no cadastro:", error);
      toast.error(t('auth.errors.unexpectedError'));
    }
  };
  
  return <div className="space-y-6">
      <div className="flex flex-col items-center space-y-4">
        <img src="/lovable-uploads/e79feb48-6e7c-488a-8626-bb79f4bde67a.png" alt="Logo" className="h-20 w-auto" />
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('auth.register.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('auth.register.subtitle')}
          </p>
        </div>
      </div>

      <Tabs defaultValue="trainer" className="w-full" onValueChange={value => setAccountType(value as "trainer" | "student")}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="trainer">Personal Trainer</TabsTrigger>
          <TabsTrigger value="student">{t('auth.student.register.title')}</TabsTrigger>
        </TabsList>
        
        <TabsContent value="trainer" className="mt-4">
          <Form {...trainerForm}>
            <form onSubmit={trainerForm.handleSubmit(onSubmitTrainer)} className="space-y-4">
              <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                <FormField control={trainerForm.control} name="firstName" render={({
                field
              }) => <FormItem>
                      <FormLabel>{t('auth.register.firstNameLabel')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input {...field} className="pl-10" placeholder={t('auth.register.firstNameLabel')} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={trainerForm.control} name="lastName" render={({
                field
              }) => <FormItem>
                      <FormLabel>{t('auth.register.lastNameLabel')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input {...field} className="pl-10" placeholder={t('auth.register.lastNameLabel')} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
              <FormField control={trainerForm.control} name="email" render={({
              field
            }) => <FormItem>
                    <FormLabel>{t('auth.register.emailLabel')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input {...field} type="email" className="pl-10" placeholder={t('auth.register.emailPlaceholder')} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={trainerForm.control} name="password" render={({
              field
            }) => <FormItem>
                    <FormLabel>{t('auth.register.passwordLabel')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input {...field} type="password" className="pl-10" placeholder={t('auth.register.passwordPlaceholder')} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                <FormField control={trainerForm.control} name="country" render={({
                field
              }) => <FormItem>
                      <FormLabel>{t('auth.register.countryLabel')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Flag className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input {...field} className="pl-10" placeholder={t('auth.register.countryPlaceholder')} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={trainerForm.control} name="city" render={({
                field
              }) => <FormItem>
                      <FormLabel>{t('auth.register.cityLabel')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input {...field} className="pl-10" placeholder={t('auth.register.cityPlaceholder')} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
              <FormField control={trainerForm.control} name="phone" render={({
              field
            }) => <FormItem>
                    <FormLabel>{t('auth.trainerFields.phoneLabel')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input {...field} className="pl-10" placeholder={t('auth.trainerFields.phonePlaceholder')} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={trainerForm.control} name="termsAccepted" render={({
              field
            }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 py-2">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm">
                        {t('auth.trainerFields.termsLabel')}
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>} />
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90">{t('auth.trainerFields.startTrial')}</Button>
              <p className="text-xs text-center text-muted-foreground">
                {t('landing.pricing.features.noCard')}
              </p>
            </form>
          </Form>
        </TabsContent>

        <TabsContent value="student" className="mt-4">
          <Form {...studentForm}>
            <form onSubmit={studentForm.handleSubmit(onSubmitStudent)} className="space-y-4">
              <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                <FormField control={studentForm.control} name="firstName" render={({
                field
              }) => <FormItem>
                      <FormLabel>{t('auth.register.firstNameLabel')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input {...field} className="pl-10" placeholder={t('auth.register.firstNameLabel')} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={studentForm.control} name="lastName" render={({
                field
              }) => <FormItem>
                      <FormLabel>{t('auth.register.lastNameLabel')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input {...field} className="pl-10" placeholder={t('auth.register.lastNameLabel')} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
              <FormField control={studentForm.control} name="email" render={({
              field
            }) => <FormItem>
                    <FormLabel>{t('auth.register.emailLabel')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input {...field} type="email" className="pl-10" placeholder={t('auth.register.emailPlaceholder')} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={studentForm.control} name="password" render={({
              field
            }) => <FormItem>
                    <FormLabel>{t('auth.register.passwordLabel')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input {...field} type="password" className="pl-10" placeholder={t('auth.register.passwordPlaceholder')} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                <FormField control={studentForm.control} name="country" render={({
                field
              }) => <FormItem>
                      <FormLabel>{t('auth.register.countryLabel')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Flag className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input {...field} className="pl-10" placeholder={t('auth.register.countryPlaceholder')} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={studentForm.control} name="city" render={({
                field
              }) => <FormItem>
                      <FormLabel>{t('auth.register.cityLabel')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input {...field} className="pl-10" placeholder={t('auth.register.cityPlaceholder')} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
              <FormField control={studentForm.control} name="termsAccepted" render={({
              field
            }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 py-2">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm">
                        {t('auth.register.termsLabel')}
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>} />
              <Button type="submit" className="w-full">
                {t('auth.register.submitButton')}
              </Button>
            </form>
          </Form>
        </TabsContent>
      </Tabs>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <Separator className="w-full" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            {t('auth.register.hasAccount') ? (language === 'pt' ? 'Ou registar com' : 'Or sign up with') : ''}
          </span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={async () => {
          const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
              redirectTo: window.location.origin,
            },
          });
          if (error) {
            console.error("Erro no registo com Google:", error);
            toast.error(language === 'pt' ? 'Erro ao registar com Google' : 'Error signing up with Google');
          }
        }}
      >
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
        Google
      </Button>

      <div className="text-center">
        <button onClick={onToggleForm} className="text-sm text-primary hover:underline">
          {t('auth.register.hasAccount')}
        </button>
      </div>
    </div>;
};
